<template>
  <browse-tabs
    v-if="showGuides || showCollections || !userNameId"
    browse
    :model-value="tab"
    :user-tabs="!!userNameId"
    :browse-id="browseId"
    @update="handleSelect"
    @toggle:filters="toggleFilterMenu"
  />

  <slot />

  <div class="tw-flex tw-w-full tw-gap-x-5" :class="Classes.BROWSE">
    <filter-menu
      v-if="allowFiltering"
      :id="browseId"
      :show="filterMenuOpen"
      :input-loading="!game?.tag_options"
      :tag-options="game?.tag_options"
      :sort-options="sortOptions"
      @update="updateTable"
    />
    <div class="tw-flex-1">
      <item-grid v-if="!hasNoResults || loading" :filter-open="filterMenuOpen">
        <item-card
          v-for="item in itemList"
          :key="item.id"
          :item="item"
          :guide-tags="tags"
          :to="{
            name: card.route,
            params: { [card.resource]: item.name_id },
          }"
          :item-type="card.resource"
        >
          <template v-if="card.footerComponent" #footer>
            <component
              :is="card.footerComponent"
              v-bind="getProps(item, card)"
              :browse-id="browseId"
            />
          </template>
        </item-card>

        <component
          :is="
            infiniteScroll ? 'infinite-scroll-container' : 'show-more-container'
          "
          :disabled="!canShowMore"
          :total="itemList.length"
          :loading="loading"
          @fetch:more="handleScroll"
        />
      </item-grid>
      <!-- No Results -->
      <no-result
        v-else-if="!loading"
        :item-name="card.resource"
        :is-filtering="hasAnyFilters"
        :user-name-id="userNameId"
        @click="resetAllFilters"
      />
    </div>

    <base-modal
      v-if="allowFiltering && filterMenuOpen"
      show
      no-margin
      no-padding
      bottom
      has-close-btn
      :close-on-escape="false"
      class="sm:tw-hidden"
      @close:modal="toggleFilterMenu"
    >
      <filter-menu
        :id="browseId"
        show
        modal
        :input-loading="!game?.tag_options"
        :tag-options="game?.tag_options"
        :sort-options="sortOptions"
        @update="updateTable"
      />
    </base-modal>
  </div>
</template>

<script>
import InfiniteScrollContainer from '@components/List/InfiniteScrollContainer.vue'
import CollectionCardFooter from '@components/List/CollectionCardFooter.vue'
import { toggleFilterMenu, filterMenuOpen } from '@composables/useFilter.js'
import { useFilter, useBrowse, useSearch, useRoute } from '@composables'
import ShowMoreContainer from '@components/List/ShowMoreContainer.vue'
import GuideCardFooter from '@components/List/GuideCardFooter.vue'
import BrowseTabs from '@components/Navigation/BrowseTabs.vue'
import ModCardFooter from '@components/List/ModCardFooter.vue'
import { onMounted, computed, toRefs, watch, ref } from 'vue'
import { BROWSE_TABS, ITEM_TYPES } from '@config/options.js'
import FilterMenu from '@components/List/FilterMenu.vue'
import NoResult from '@components/List/NoResult.vue'
import ItemGrid from '@components/List/ItemGrid.vue'
import ItemCard from '@components/List/ItemCard.vue'
import Classes from '@shared/js/config/css.js'
import { useBreakpoints } from '@vueuse/core'
import { getProps } from '@helpers/utils.js'
import { Settings } from '@config/game.js'
import { BREAKPOINTS } from '@config'

function getTabFromResource(resource) {
  switch (resource) {
    case ITEM_TYPES.GUIDE:
      return BROWSE_TABS.GUIDES
    case ITEM_TYPES.COLLECTION:
      return BROWSE_TABS.COLLECTIONS
    default:
      return BROWSE_TABS.MODS
  }
}

export default {
  components: {
    InfiniteScrollContainer,
    CollectionCardFooter,
    ShowMoreContainer,
    GuideCardFooter,
    ModCardFooter,
    BrowseTabs,
    FilterMenu,
    ItemCard,
    ItemGrid,
    NoResult,
  },
  props: {
    game: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Object,
      default: null,
    },
    card: {
      type: Object,
      required: true,
    },
    total: {
      type: Number,
      default: 0,
    },
    browseId: {
      type: String,
      required: true,
    },
    sortOptions: {
      type: Array,
      required: true,
    },
    refresh: {
      type: Boolean,
      default: true,
    },
    initialSort: {
      type: Boolean,
      default: false,
    },
    allowReset: {
      type: Boolean,
      default: true,
    },
    tags: {
      type: Object,
      default: undefined,
    },
  },
  emits: ['fetch:paginate-prev', 'fetch:paginate-next', 'fetch:query'],
  setup(props) {
    const { sortOptions, initialSort, browseId, refresh, items, total, card } =
      toRefs(props)

    const { getUserNameId } = useRoute()
    const userNameId = getUserNameId().value

    const { greaterOrEqual } = useBreakpoints(BREAKPOINTS)
    const smAndUp = greaterOrEqual('sm')

    const infiniteScroll = computed(() => Settings.value.allowInfiniteScroll)
    const showCollections = computed(() => Settings.value.showCollections)
    const allowFiltering = computed(() => Settings.value.allowFiltering)
    const showGuides = computed(() => Settings.value.showGuides)
    const limit = computed(() => Settings.value.ugcPerPage)

    const tab = ref(getTabFromResource(card.value.resource))

    const {
      handleFilterUpdate,
      handleScroll,
      updateTable,
      canShowMore,
      itemList,
      range,
    } = useBrowse({
      id: browseId.value,
      items,
      fetch: computed(() => true),
      total,
      sortOptions: sortOptions.value,
      initialSort,
      limit,
      refresh: refresh.value,
      infinite: true,
      browsePage: true,
      isGuides: card.value.resource === ITEM_TYPES.GUIDE,
    })

    const { clearUserSearch, clearSearch, search, searchUserName } = useSearch(
      browseId.value
    )

    const { watchFilters, activeFiltersCount, resetFilter } = useFilter(
      browseId.value
    )

    watch(smAndUp, (_smAndUp) => {
      if (allowFiltering.value && _smAndUp !== filterMenuOpen.value) {
        toggleFilterMenu()
      }
    })

    function handleSelect(input) {
      tab.value = input
    }

    // Triggers fetch on parent component
    onMounted(() => {
      watchFilters(() => {
        handleFilterUpdate()
        updateTable('filter')
      }, false)
      if (allowFiltering.value && !smAndUp.value && filterMenuOpen.value) {
        toggleFilterMenu()
      }
    })
    const hasNoResults = computed(() => itemList.value?.length === 0)

    const hasAnyFilters = computed(
      () =>
        !!activeFiltersCount.value || !!search.value || !!searchUserName.value
    )

    function resetAllFilters() {
      resetFilter()
      clearSearch()
      clearUserSearch()
      updateTable('search')
    }

    return {
      activeFiltersCount,
      toggleFilterMenu,
      resetAllFilters,
      showCollections,
      infiniteScroll,
      filterMenuOpen,
      allowFiltering,
      hasAnyFilters,
      hasNoResults,
      handleScroll,
      handleSelect,
      updateTable,
      canShowMore,
      ITEM_TYPES,
      userNameId,
      showGuides,
      Settings,
      itemList,
      getProps,
      Classes,
      // required for unit test
      range,
      tab,
    }
  },
}
</script>
